import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Pencil, Home, Users, Tractor, Sprout, Droplets } from "lucide-react";

function age(dob) {
  if (!dob) return "—";
  const d = new Date(dob);
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a -= 1;
  return a;
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center">
          <Icon className="h-4 w-4 text-graphite" />
        </div>
        <h2 className="text-lg font-heading font-medium text-graphite">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm font-medium text-graphite mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

export default function FamilyDetail() {
  const { familyId } = useParams();
  const { t } = useLang();
  const { scope } = useAuth();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);

  useEffect(() => {
    api.get(`/families/${familyId}`, { params: { scope } })
      .then(({ data }) => setFamily(data))
      .catch((err) => {
        toast.error(formatApiError(err));
        navigate("/families");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, scope]);

  if (!family) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-10 w-10 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
      </div>
    );
  }

  const head = family.members.find((m) => m.relation === "Self") || family.members[0];
  const totalCents = family.land_parcels.reduce((s, p) => s + (p.acres || 0) * 100 + (p.cents || 0), 0);
  const yesNo = (v) => (v ? t("yes") : t("no"));

  return (
    <div className="space-y-5" data-testid="family-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button data-testid="back-btn" variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="family-detail-id">{family.family_id}</h1>
              <Badge variant="outline" className="text-xs">{family.caste}</Badge>
              {family.is_demo && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs" variant="outline">{t("demo_mode")}</Badge>}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {head?.name} · {family.ward} · {family.address}
            </p>
          </div>
        </div>
        <Button data-testid="edit-family-btn" className="bg-brand-red hover:bg-brand-reddark text-white transition-colors duration-200" onClick={() => navigate(`/families/${family.family_id}/edit`)}>
          <Pencil className="h-4 w-4 mr-2" />{t("edit")}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Section icon={Home} title={t("housing_details")} testid="detail-housing">
          <div className="grid grid-cols-2 gap-4">
            <KV label={t("f_own_house")} value={yesNo(family.own_house)} />
            <KV label={t("f_house_type")} value={family.house_type} />
            <KV label={t("f_patta_issued")} value={yesNo(family.patta_issued)} />
            <KV label={t("f_patta_number")} value={family.patta_number} />
            <KV label={t("f_needs_patta")} value={yesNo(family.needs_new_house_patta)} />
          </div>
        </Section>

        <Section icon={Tractor} title={t("agriculture_details")} testid="detail-agriculture">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <KV label={t("f_owns_land")} value={yesNo(family.owns_agri_land)} />
            <KV label={t("total_land")} value={`${Math.floor(totalCents / 100)} ${t("p_acres")} ${totalCents % 100} ${t("p_cents")}`} />
            <KV label={t("f_needs_borewell")} value={yesNo(family.needs_borewell)} />
          </div>
          {family.land_parcels.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">{t("p_survey")}</TableHead>
                  <TableHead className="text-xs text-right">{t("p_acres")}</TableHead>
                  <TableHead className="text-xs text-right">{t("p_cents")}</TableHead>
                  <TableHead className="text-xs">{t("p_land_type")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {family.land_parcels.map((p) => (
                  <TableRow key={p.parcel_id} data-testid={`parcel-${p.parcel_id}`}>
                    <TableCell className="text-sm font-mono">{p.survey_number}</TableCell>
                    <TableCell className="text-sm text-right">{p.acres}</TableCell>
                    <TableCell className="text-sm text-right">{p.cents}</TableCell>
                    <TableCell><Badge variant="outline" className={p.land_type === "Irrigated" ? "text-water border-water/30" : "text-amber-700 border-amber-300"}>{p.land_type}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      </div>

      <Section icon={Users} title={`${t("family_members")} (${family.members.length})`} testid="detail-members">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs">{t("m_name")}</TableHead>
              <TableHead className="text-xs">{t("m_relation")}</TableHead>
              <TableHead className="text-xs">{t("m_gender")}</TableHead>
              <TableHead className="text-xs text-center">{t("m_age")}</TableHead>
              <TableHead className="text-xs">{t("m_marital")}</TableHead>
              <TableHead className="text-xs">{t("m_education")}</TableHead>
              <TableHead className="text-xs">{t("m_profession")}</TableHead>
              <TableHead className="text-xs">{t("m_caste")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {family.members.map((m) => (
              <TableRow key={m.member_id} data-testid={`member-${m.member_id}`} className={m.relation === "Self" ? "bg-red-50/40" : ""}>
                <TableCell className="text-sm font-medium text-graphite">
                  {m.name}
                  {m.relation === "Self" && <Badge className="ml-2 bg-brand-red text-white text-[10px]">{t("head_label")}</Badge>}
                </TableCell>
                <TableCell className="text-xs">{m.relation}</TableCell>
                <TableCell className="text-xs">{m.gender}</TableCell>
                <TableCell className="text-xs text-center">{age(m.dob)}</TableCell>
                <TableCell className="text-xs">{m.marital_status}</TableCell>
                <TableCell className="text-xs">{m.education}</TableCell>
                <TableCell className="text-xs">{m.profession}</TableCell>
                <TableCell className="text-xs">{m.caste}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <div className="grid md:grid-cols-2 gap-5">
        <Section icon={Sprout} title={`${t("crops_details")} (${family.crops.length})`} testid="detail-crops">
          {family.crops.length === 0 ? (
            <p className="text-sm text-slate-400">{t("na")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">{t("c_crop")}</TableHead>
                  <TableHead className="text-xs">{t("c_season")}</TableHead>
                  <TableHead className="text-xs">{t("c_year")}</TableHead>
                  <TableHead className="text-xs text-right">{t("p_acres")}</TableHead>
                  <TableHead className="text-xs text-right">{t("p_cents")}</TableHead>
                  <TableHead className="text-xs">{t("c_irrigation")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {family.crops.map((c, i) => (
                  <TableRow key={i} data-testid={`crop-row-${i}`}>
                    <TableCell className="text-sm font-medium text-agri">{c.crop}</TableCell>
                    <TableCell className="text-xs">{c.season}</TableCell>
                    <TableCell className="text-xs">{c.year}</TableCell>
                    <TableCell className="text-xs text-right">{c.acres}</TableCell>
                    <TableCell className="text-xs text-right">{c.cents}</TableCell>
                    <TableCell className="text-xs">{c.irrigation_source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>

        <Section icon={Droplets} title={`${t("borewells_details")} (${family.borewells.length})`} testid="detail-borewells">
          {family.borewells.length === 0 ? (
            <p className="text-sm text-slate-400">{t("na")}</p>
          ) : (
            <div className="space-y-3">
              {family.borewells.map((b) => (
                <div key={b.borewell_id} className="border border-slate-200 rounded-md p-4" data-testid={`borewell-${b.borewell_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={b.borewell_type === "Government" ? "text-water border-water/30" : "text-graphite"}>{b.borewell_type}</Badge>
                      <Badge variant="outline" className={b.status === "Working" ? "text-agri border-agri/30" : "text-brand-red border-brand-red/30"}>{b.status}</Badge>
                    </div>
                    <span className="text-xs text-slate-400">{b.depth_ft} ft</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <KV label={t("b_electricity")} value={yesNo(b.electricity_connection)} />
                    <KV label={t("b_service_no")} value={b.service_number} />
                    <KV label={t("b_conn_date")} value={b.connection_date} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
